import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import LegalService from "../services/LegalService";
import { TermsAndConditions, PrivacyPolicy } from "../types";
import "./LegalAcceptance.css"; // Import CSS for styling legal content
import {
  OwcTypography,
  OwcButton,
  OwcCard,
  OwcCheckbox,
  OwcDivider,
  OwcProgressSpinner,
  OwcTabs,
  OwcTab,
} from "@one/react";

const LegalAcceptance: React.FC = () => {
  const { authState, acceptTerms, acceptPrivacy } = useAuth();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [terms, setTerms] = useState<TermsAndConditions | null>(null);
  const [privacy, setPrivacy] = useState<PrivacyPolicy | null>(null);
  const [activeTab, setActiveTab] = useState<string>("terms");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  // Create refs for our @one components
  const tabsRef = useRef<HTMLOwcTabsElement>(null);
  const termsCheckboxRef = useRef<HTMLOwcCheckboxElement>(null);
  const privacyCheckboxRef = useRef<HTMLOwcCheckboxElement>(null);

  useEffect(() => {
    const fetchLegalDocuments = async () => {
      try {
        setIsLoading(true);
        const [termsData, privacyData] = await Promise.all([
          LegalService.getLatestTerms(),
          LegalService.getLatestPrivacy(),
        ]);

        setTerms(termsData);
        setPrivacy(privacyData);
        setError(null);
      } catch (err) {
        setError("Failed to load legal documents. Please try again.");
        console.error("Error fetching legal documents:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLegalDocuments();
  }, []);

  // If user has already accepted both documents, redirect to home
  useEffect(() => {
    if (
      authState.hasAcceptedLatestTerms &&
      authState.hasAcceptedLatestPrivacy &&
      !isLoading
    ) {
      navigate("/patients");
    }
  }, [
    authState.hasAcceptedLatestTerms,
    authState.hasAcceptedLatestPrivacy,
    navigate,
    isLoading,
  ]);

  const handleTabChange = (event: CustomEvent) => {
    // Get the value from the custom event detail
    if (event.detail) {
      setActiveTab(event.detail);
    }
  };

  const handleAccept = async () => {
    if (!terms || !privacy || !termsAccepted || !privacyAccepted) {
      return;
    }

    try {
      setIsLoading(true);
      // Accept both documents
      await Promise.all([
        acceptTerms(terms.version),
        acceptPrivacy(privacy.version),
      ]);

      // Navigate to patient list
      navigate("/patients");
    } catch (err) {
      setError("Failed to save your acceptance. Please try again.");
      console.error("Error accepting legal documents:", err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="owc-container" style={{ maxWidth: "800px" }}>
        <div
          className="owc-flex owc-justify-center owc-align-center"
          style={{ minHeight: "400px" }}
        >
          <OwcProgressSpinner size="large"></OwcProgressSpinner>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="owc-container" style={{ maxWidth: "800px" }}>
        <OwcCard>
          <div className="owc-p-5">
            <OwcTypography
              style={{
                color: "var(--owc-color-critical)",
                marginBottom: "16px",
              }}
            >
              {error}
            </OwcTypography>
            <OwcButton
              variant="primary"
              size="medium"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </OwcButton>
          </div>
        </OwcCard>
      </div>
    );
  }

  return (
    <div className="owc-container" style={{ maxWidth: "800px" }}>
      <OwcCard style={{ marginTop: "32px", overflow: "hidden" }}>
        <div style={{ padding: "16px" }}>
          <OwcTypography style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
            Please Review and Accept
          </OwcTypography>
        </div>

        <OwcTabs
          ref={tabsRef}
          value={activeTab}
          onValueChange={handleTabChange}
          style={{
            borderBottom: "1px solid var(--owc-color-neutral-20)",
            width: "100%",
          }}
        >
          <OwcTab value="terms">Terms & Conditions</OwcTab>
          <OwcTab value="privacy">Privacy Policy</OwcTab>
        </OwcTabs>

        <OwcDivider />

        <div style={{ padding: "16px", maxHeight: "400px", overflow: "auto" }}>
          {activeTab === "terms" && terms && (
            <>
              <OwcTypography
                variant="subtitle1"
                style={{
                  fontWeight: "bold",
                  marginBottom: "16px",
                }}
              >
                Version {terms.version} - Effective Date:{" "}
                {new Date(terms.effectiveDate).toLocaleDateString()}
              </OwcTypography>
              <div
                className="owc-legal-content"
                dangerouslySetInnerHTML={{ __html: terms.content }}
              />
            </>
          )}

          {activeTab === "privacy" && privacy && (
            <>
              <OwcTypography
                variant="subtitle1"
                style={{
                  fontWeight: "bold",
                  marginBottom: "16px",
                }}
              >
                Version {privacy.version} - Effective Date:{" "}
                {new Date(privacy.effectiveDate).toLocaleDateString()}
              </OwcTypography>
              <div
                className="owc-legal-content"
                style={{
                  fontFamily: "var(--owc-font-family)",
                  fontSize: "var(--owc-font-size-body)",
                  lineHeight: "var(--owc-line-height-body)",
                  color: "var(--owc-color-text)",
                }}
                dangerouslySetInnerHTML={{ __html: privacy.content }}
              />
            </>
          )}
        </div>

        <OwcDivider />

        <div style={{ padding: "16px" }}>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <OwcCheckbox
                ref={termsCheckboxRef}
                checked={termsAccepted}
                onValueChange={(event: CustomEvent<boolean>) => {
                  setTermsAccepted(event.detail);
                }}
              />
              <OwcTypography style={{ marginLeft: "8px" }}>
                I have read and agree to the Terms and Conditions
              </OwcTypography>
            </div>

            <div style={{ display: "flex", alignItems: "center" }}>
              <OwcCheckbox
                ref={privacyCheckboxRef}
                checked={privacyAccepted}
                onValueChange={(event: CustomEvent<boolean>) => {
                  setPrivacyAccepted(event.detail);
                }}
              />
              <OwcTypography style={{ marginLeft: "8px" }}>
                I have read and agree to the Privacy Policy
              </OwcTypography>
            </div>

            <div style={{ display: "flex", marginTop: "20px" }}>
              <OwcButton
                variant="primary"
                style={{ minWidth: "180px" }}
                size="medium"
                disabled={!termsAccepted || !privacyAccepted}
                onClick={handleAccept}
              >
                Accept and Continue
              </OwcButton>
            </div>
          </div>
        </div>
      </OwcCard>
    </div>
  );
};

export default LegalAcceptance;
